import { type ReactNode } from 'react';
import { allTypes } from '@/mocks/dramas';
import { regions, years } from '@/mocks/categories';

interface FilterPanelProps {
  selectedTypes: string[];
  selectedRegions: string[];
  selectedYears: number[];
  onToggleType: (value: string) => void;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onClear: () => void;
}

function FilterGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="py-5 border-b border-background-200 last:border-b-0">
      <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-3">{title}</h4>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs md:text-sm transition-colors whitespace-nowrap cursor-pointer border ${
        active
          ? 'bg-primary-500 border-primary-500 text-background-50'
          : 'bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300'
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterPanel({
  selectedTypes,
  selectedRegions,
  selectedYears,
  onToggleType,
  onToggleRegion,
  onToggleYear,
  onClear,
}: FilterPanelProps) {
  const hasFilter =
    selectedTypes.length > 0 || selectedRegions.length > 0 || selectedYears.length > 0;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-600" />
          筛选条件
        </h3>
        {hasFilter && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-accent-600 hover:text-accent-700 inline-flex items-center gap-1 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-close-circle-line" />
            清除
          </button>
        )}
      </div>

      <FilterGroup title="类型">
        {allTypes.map((type) => (
          <Chip key={type} active={selectedTypes.includes(type)} onClick={() => onToggleType(type)}>
            {type}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup title="地区">
        {regions.map((region) => (
          <Chip key={region} active={selectedRegions.includes(region)} onClick={() => onToggleRegion(region)}>
            {region}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup title="年份">
        {years.map((year) => (
          <Chip key={year} active={selectedYears.includes(year)} onClick={() => onToggleYear(year)}>
            {year}
          </Chip>
        ))}
      </FilterGroup>
    </div>
  );
}